import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { compradorTrpc } from '../../lib/compradorTrpc';
import { sessaoCompradorAtual } from '../../lib/compradorAuth';
import { AppShellComprador } from '../../components/comprador/AppShellComprador';
import { InstalarIconeHeader } from '../../components/comprador/InstalarIconeHeader';
import { useCompradorSessao, sairParaLogin } from './util';

interface Mensagem {
  id: string;
  autor: 'comprador' | 'agente';
  corpo: string;
  pedidoBoletoId: string | null;
}

/**
 * Conversa (design aprovado): chat com o assistente da incorporadora. O
 * agente explica com os números reais; o chip "2ª via do boleto" chama a
 * rota determinística (o LLM nunca registra nada). Mensagem do agente com
 * pedidoBoletoId atendido vira botão de download.
 */
export function ConversaComprador() {
  const slug = useCompradorSessao();
  const [, navigate] = useLocation();
  const [corMarca, setCorMarca] = useState<string | null>(null);
  const [orgNome, setOrgNome] = useState('');
  const [mensagens, setMensagens] = useState<Mensagem[]>([]);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const fimRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!slug) return;
    let vivo = true;
    Promise.all([
      compradorTrpc.auth.me.query({ orgSlug: slug }),
      compradorTrpc.app.conversa.query({ orgSlug: slug }),
    ])
      .then(([m, msgs]) => {
        if (!vivo) return;
        setCorMarca(m.corMarca);
        setOrgNome(m.orgNome);
        setMensagens(msgs as Mensagem[]);
      })
      .catch(() => sairParaLogin(slug, navigate))
      .finally(() => vivo && setCarregando(false));
    return () => {
      vivo = false;
    };
  }, [slug, navigate]);

  useEffect(() => {
    fimRef.current?.scrollIntoView({ block: 'end' });
  }, [mensagens.length, enviando]);

  async function enviar(corpo: string) {
    if (!slug || !corpo.trim() || enviando) return;
    setTexto('');
    setEnviando(true);
    const otimista: Mensagem = { id: `tmp-${Date.now()}`, autor: 'comprador', corpo: corpo.trim(), pedidoBoletoId: null };
    setMensagens((m) => [...m, otimista]);
    try {
      await compradorTrpc.app.enviarMensagem.mutate({ orgSlug: slug, corpo: corpo.trim() });
      const msgs = await compradorTrpc.app.conversa.query({ orgSlug: slug });
      setMensagens(msgs as Mensagem[]);
    } catch (e) {
      setMensagens((m) => [
        ...m,
        { id: `err-${Date.now()}`, autor: 'agente', corpo: (e as Error).message || 'Não consegui responder agora. Tente de novo.', pedidoBoletoId: null },
      ]);
    } finally {
      setEnviando(false);
    }
  }

  async function pedirBoletoRapido() {
    if (!slug || enviando) return;
    setEnviando(true);
    try {
      // Pede a 2ª via da PRÓXIMA parcela em aberto (rota determinística).
      const { parcelas } = await compradorTrpc.app.parcelas.query({ orgSlug: slug });
      const aberta = (parcelas as { id: string; statusPagamento: string; vencimento: string | null }[])
        .filter((p) => p.statusPagamento !== 'paga')
        .sort((a, b) => (a.vencimento ?? '9999').localeCompare(b.vencimento ?? '9999'))[0];
      if (!aberta) {
        setMensagens((m) => [
          ...m,
          { id: `ok-${Date.now()}`, autor: 'agente', corpo: 'Você não tem nenhuma parcela em aberto agora. Tudo em dia!', pedidoBoletoId: null },
        ]);
        return;
      }
      await compradorTrpc.app.pedirBoleto.mutate({ orgSlug: slug, parcelaId: aberta.id });
      const msgs = await compradorTrpc.app.conversa.query({ orgSlug: slug });
      setMensagens(msgs as Mensagem[]);
    } catch {
      setMensagens((m) => [
        ...m,
        { id: `err-${Date.now()}`, autor: 'agente', corpo: 'Não consegui registrar o pedido agora. Tente de novo em instantes.', pedidoBoletoId: null },
      ]);
    } finally {
      setEnviando(false);
    }
  }

  async function baixarBoleto(pedidoId: string) {
    if (!slug) return;
    const token = sessaoCompradorAtual(slug);
    const res = await fetch(`/api/comprador/boleto/${pedidoId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'boleto.pdf';
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <AppShellComprador corMarca={corMarca} semPadding>
      {/* Altura exata: viewport menos a navbar inferior (h-16) no mobile —
          sem isso o input do chat ficaria escondido atrás da barra fixa. */}
      <div className="flex h-[calc(100dvh-4rem)] flex-col md:h-dvh" data-testid="conversa-comprador">
        {/* Cabeçalho */}
        <header className="flex items-center gap-3 border-b border-line bg-surface px-5 py-3.5">
          <span
            className="flex h-10 w-10 items-center justify-center rounded-full"
            style={{ background: 'var(--brand-soft)', color: 'var(--brand)' }}
            aria-hidden="true"
          >
            <svg viewBox="0 0 24 24" width="19" height="19" fill="none">
              <path
                d="M12 3.5l1.8 4.3 4.7.4-3.6 3.1 1.1 4.6-4-2.5-4 2.5 1.1-4.6-3.6-3.1 4.7-.4z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <div>
            <div className="text-[15px] font-semibold text-ink">Assistente {orgNome}</div>
            <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--brand)' }}>
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: 'var(--brand)' }} />
              responde na hora
            </div>
          </div>
          <div className="ml-auto">
            <InstalarIconeHeader orgNome={orgNome} corMarca={corMarca} />
          </div>
        </header>

        {/* Mensagens */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {carregando ? (
            <p className="py-16 text-center text-sm text-ink-3">Carregando…</p>
          ) : (
            <>
              {mensagens.length === 0 && (
                <div className="mr-10 rounded-2xl rounded-tl-md bg-surface p-3.5 text-sm leading-relaxed text-ink-2 shadow-sm" data-testid="conversa-boas-vindas">
                  Oi! Estou aqui para o que precisar sobre o seu imóvel. Pode perguntar à vontade.
                </div>
              )}
              <div className="space-y-3">
                {mensagens.map((m) =>
                  m.autor === 'comprador' ? (
                    <div key={m.id} className="ml-10 flex justify-end">
                      <div
                        className="rounded-2xl rounded-tr-md px-3.5 py-2.5 text-sm leading-relaxed text-white"
                        style={{ background: 'var(--brand)' }}
                      >
                        {m.corpo}
                      </div>
                    </div>
                  ) : (
                    <div key={m.id} className="mr-10">
                      <div className="rounded-2xl rounded-tl-md bg-surface px-3.5 py-2.5 text-sm leading-relaxed text-ink-2 shadow-sm">
                        <span className="whitespace-pre-line">{m.corpo}</span>
                        {m.pedidoBoletoId && (
                          <button
                            type="button"
                            onClick={() => baixarBoleto(m.pedidoBoletoId!)}
                            className="mt-2.5 block w-full rounded-xl py-2 text-center text-[13px] font-semibold text-white"
                            style={{ background: 'var(--brand)' }}
                            data-testid="baixar-boleto"
                          >
                            Baixar boleto
                          </button>
                        )}
                      </div>
                    </div>
                  ),
                )}
                {enviando && (
                  <div className="mr-10">
                    <div className="inline-block rounded-2xl rounded-tl-md bg-surface px-3.5 py-2.5 text-sm text-ink-4 shadow-sm">
                      escrevendo…
                    </div>
                  </div>
                )}
              </div>
              <div ref={fimRef} />
            </>
          )}
        </div>

        {/* Chips + input */}
        <div className="border-t border-line bg-surface px-4 pb-4 pt-3">
          <div className="mb-2.5 flex gap-2">
            <button
              type="button"
              disabled={enviando}
              onClick={() => enviar('Quanto vai subir minha parcela com o reajuste?')}
              className="rounded-full px-3.5 py-1.5 text-[12.5px] font-medium disabled:opacity-60"
              style={{ background: 'var(--brand-soft)', color: 'var(--brand)' }}
            >
              Meu reajuste
            </button>
            <button
              type="button"
              disabled={enviando}
              onClick={pedirBoletoRapido}
              className="rounded-full border px-3.5 py-1.5 text-[12.5px] font-medium disabled:opacity-60"
              style={{ borderColor: 'var(--line-2)', color: 'var(--ink-2)' }}
              data-testid="chip-boleto"
            >
              2ª via do boleto
            </button>
          </div>
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              void enviar(texto);
            }}
          >
            <input
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder="Escreva sua mensagem"
              aria-label="Escreva sua mensagem"
              className="h-11 flex-1 rounded-full border border-line-2 bg-bg-warm px-4 text-sm text-ink outline-none focus:border-accent"
              data-testid="conversa-input"
            />
            <button
              type="submit"
              disabled={enviando || !texto.trim()}
              aria-label="Enviar mensagem"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-white disabled:opacity-50"
              style={{ background: 'var(--brand)' }}
              data-testid="conversa-enviar"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
                <path d="M5 12h13M13 6.5L18.5 12 13 17.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </form>
        </div>
      </div>
    </AppShellComprador>
  );
}
